// SNIGJ - Appearance Context for global styling (fonts, colors, size)
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AppearanceContext = createContext(null);

// Font options with Google Fonts URLs
const FONTS = [
  { value: 'inter', label: 'Inter (Défaut)', family: "'Inter', sans-serif", url: 'Inter:wght@400;500;600;700' },
  { value: 'roboto', label: 'Roboto', family: "'Roboto', sans-serif", url: 'Roboto:wght@400;500;700' },
  { value: 'poppins', label: 'Poppins', family: "'Poppins', sans-serif", url: 'Poppins:wght@400;500;600;700' },
  { value: 'opensans', label: 'Open Sans', family: "'Open Sans', sans-serif", url: 'Open+Sans:wght@400;500;600;700' },
  { value: 'lato', label: 'Lato', family: "'Lato', sans-serif", url: 'Lato:wght@400;700' },
  { value: 'montserrat', label: 'Montserrat', family: "'Montserrat', sans-serif", url: 'Montserrat:wght@400;500;600;700' }
];

// Color presets
const COLOR_PRESETS = [
  { name: 'Justice (Défaut)', primary: '#eab308', secondary: '#1e3a5f', accent: '#d4af37' },
  { name: 'Forêt', primary: '#22c55e', secondary: '#14532d', accent: '#86efac' },
  { name: 'Océan', primary: '#0ea5e9', secondary: '#0c4a6e', accent: '#7dd3fc' },
  { name: 'Royal', primary: '#8b5cf6', secondary: '#4c1d95', accent: '#c4b5fd' },
  { name: 'Feu', primary: '#ef4444', secondary: '#7f1d1d', accent: '#fca5a5' },
  { name: 'Nuit', primary: '#6366f1', secondary: '#1e1b4b', accent: '#a5b4fc' }
];

const DEFAULT_SETTINGS = {
  font: 'inter',
  fontSize: 16,
  primaryColor: '#eab308',
  secondaryColor: '#1e3a5f',
  accentColor: '#d4af37'
};

// Convert hex to HSL for Tailwind CSS variables
const hexToHSL = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0 0% 0%';
  
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;
  
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

export const AppearanceProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    // Load from localStorage on init
    return {
      font: localStorage.getItem('snigj_font') || DEFAULT_SETTINGS.font,
      fontSize: parseInt(localStorage.getItem('snigj_fontSize') || DEFAULT_SETTINGS.fontSize),
      primaryColor: localStorage.getItem('snigj_primaryColor') || DEFAULT_SETTINGS.primaryColor,
      secondaryColor: localStorage.getItem('snigj_secondaryColor') || DEFAULT_SETTINGS.secondaryColor,
      accentColor: localStorage.getItem('snigj_accentColor') || DEFAULT_SETTINGS.accentColor
    };
  });

  // Apply styles to document immediately
  const applyStyles = useCallback((newSettings) => {
    const root = document.documentElement;
    const font = FONTS.find(f => f.value === newSettings.font) || FONTS[0];
    
    // Load font if not default
    if (newSettings.font !== 'inter') {
      const linkId = 'snigj-custom-font';
      let fontLink = document.getElementById(linkId);
      if (!fontLink) {
        fontLink = document.createElement('link');
        fontLink.id = linkId;
        fontLink.rel = 'stylesheet';
        document.head.appendChild(fontLink);
      }
      fontLink.href = `https://fonts.googleapis.com/css2?family=${font.url}&display=swap`;
    }
    
    // Apply font family to body and all text elements
    document.body.style.fontFamily = font.family;
    root.style.setProperty('--app-font-family', font.family);
    
    // Apply font size
    const baseFontSize = newSettings.fontSize || 16;
    root.style.fontSize = `${baseFontSize}px`;
    document.body.style.fontSize = `${baseFontSize}px`;
    
    // Apply custom accent color (for buttons, links, etc.)
    const accentHSL = hexToHSL(newSettings.accentColor);
    root.style.setProperty('--accent', accentHSL);
    
    // Store custom colors as CSS variables for components that need them
    root.style.setProperty('--custom-primary', newSettings.primaryColor);
    root.style.setProperty('--custom-secondary', newSettings.secondaryColor);
    root.style.setProperty('--custom-accent', newSettings.accentColor);
    
    // Apply primary color to ring/focus states
    const primaryHSL = hexToHSL(newSettings.primaryColor);
    root.style.setProperty('--ring', primaryHSL);
    
    console.log('Applied appearance settings:', newSettings);
  }, []);

  // Apply on mount and when settings change
  useEffect(() => {
    applyStyles(settings);
  }, [settings, applyStyles]);

  // Update a single setting
  const updateSetting = useCallback((key, value) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      localStorage.setItem(`snigj_${key}`, value.toString());
      return newSettings;
    });
  }, []);

  // Update all settings at once
  const updateSettings = useCallback((newSettings) => {
    setSettings(prev => {
      const merged = { ...prev, ...newSettings };
      Object.keys(newSettings).forEach(key => {
        localStorage.setItem(`snigj_${key}`, newSettings[key].toString());
      });
      return merged;
    });
  }, []);

  // Apply a color preset
  const applyPreset = useCallback((preset) => {
    updateSettings({
      primaryColor: preset.primary,
      secondaryColor: preset.secondary,
      accentColor: preset.accent
    });
  }, [updateSettings]);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    Object.keys(DEFAULT_SETTINGS).forEach(key => {
      localStorage.removeItem(`snigj_${key}`);
    });
    setSettings(DEFAULT_SETTINGS);
  }, []);

  const value = {
    settings,
    updateSetting,
    updateSettings,
    applyPreset,
    resetToDefaults,
    fonts: FONTS,
    colorPresets: COLOR_PRESETS,
    defaults: DEFAULT_SETTINGS
  };

  return (
    <AppearanceContext.Provider value={value}>
      {children}
    </AppearanceContext.Provider>
  );
};

export const useAppearance = () => {
  const context = useContext(AppearanceContext);
  if (!context) {
    throw new Error('useAppearance must be used within an AppearanceProvider');
  }
  return context;
};

export default AppearanceContext;
